import React from "react";
import Ring, { RingColors, themeColors, polarToCartesian, describeSegment } from "./Ring";
import { AppTheme } from "./App";
import {
  HolidayKey,
  wheelColors,
  MemeGuestContext,
  MEME_GUEST_NAMES,
  WHEEL_ICONS,
  WHEEL_REVEAL_ICONS,
  WHEEL_REVEAL_SCALE,
} from "./holidays";
import { LightningStrike } from "./holidays/icons/LightningStrike";

interface WheelProps {
  theme: AppTheme;
  onClick: () => void;
  innerNames: string[];
  outerNames: string[];
  thirdNames?: string[]; // Optional third ring of names
  innerRotation: number;
  outerRotation: number;
  thirdRotation?: number; // Optional third ring rotation
  trioMode?: boolean; // Flag to indicate if we're in trio mode
  /** Aktivt högtidstema. Ger hjulet temats ljusa nyans och högtidens ikon i mitten. */
  holiday?: HolidayKey | null;
}

const Wheel: React.FC<WheelProps> = ({
  theme,
  onClick,
  innerNames,
  outerNames,
  thirdNames = [],
  innerRotation,
  outerRotation,
  thirdRotation = 0,
  trioMode = false,
  holiday = null,
}) => {
  const [buttonRotation, setButtonRotation] = React.useState<number>(0);

  // Mittknappen snurrar ett varv bara när hjulet faktiskt snurras. Rotationen
  // nollställs (0) när vyn öppnas – då ska mitten stå still.
  React.useEffect(() => {
    if (innerRotation !== 0) {
      setButtonRotation(prev => prev + 360);
    }
  }, [innerRotation]);

  // Har hjulet snurrat klart? Varje snurr ger en ny rotation som animeras
  // i 6 s (.animated-rotation i App.css). För högtider med
  // en avslöjad ikon slår en blixt ner i mitten först ("strike"), och
  // ikonen byts mitt i nedslaget.
  const hasReveal = !!(holiday && WHEEL_REVEAL_ICONS[holiday]);
  const [settled, setSettled] = React.useState(false);
  const [striking, setStriking] = React.useState(false);
  React.useEffect(() => {
    setSettled(false);
    setStriking(false);
    if (innerRotation === 0) return;
    const reduceMotion =
      typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!hasReveal || reduceMotion) {
      const t = window.setTimeout(() => setSettled(true), 6100);
      return () => window.clearTimeout(t);
    }
    const timers = [
      window.setTimeout(() => setStriking(true), 6100),
      window.setTimeout(() => setSettled(true), 6100 + 470),
      window.setTimeout(() => setStriking(false), 6100 + 1700),
    ];
    return () => timers.forEach(t => window.clearTimeout(t));
  }, [innerRotation, hasReveal]);

  // Halloween: varje meme i mitten (Johnny, Voldemort, …, Palpatine) bjuder
  // in sig själv till en slumpad grupp första gången den visas
  // efter ett snurr. Memen väljer bort skurkar så att alla får en grupp.
  // Namnen ritas bara i hjulet – de hamnar aldrig i namnlistan eller länken.
  const slotCount = innerNames.length;
  const [guests, setGuests] = React.useState<{ name: string; slot: number }[]>([]);
  React.useEffect(() => {
    setGuests([]);
  }, [innerRotation, slotCount, holiday]);
  const addGuest = React.useCallback(
    (name: string) =>
      setGuests(prev => {
        if (prev.some(g => g.name === name)) return prev;
        const free = Array.from({ length: slotCount }, (_, i) => i).filter(i => !prev.some(g => g.slot === i));
        if (free.length === 0) return prev;
        return [...prev, { name, slot: free[Math.floor(Math.random() * free.length)] }];
      }),
    [slotCount]
  );
  const guestContext = React.useMemo(() => ({ addGuest, groups: slotCount }), [addGuest, slotCount]);

  // Calculate radii based on whether we're in trio mode or not
  const innerRadius = trioMode ? 152 : 182;
  const middleRadiusInner = trioMode ? 232 : 266;
  const middleRadiusOuter = trioMode ? 296 : 336;
  const outerRadiusOuter = 350;

  const hol = holiday ? wheelColors(holiday, theme !== "bw") : null;
  const ringColors: RingColors | undefined = hol
    ? {
        segmentFrom: hol.segmentFrom,
        segmentTo: hol.segmentTo,
        segmentStroke: hol.segmentStroke,
        text: hol.text,
      }
    : undefined;

  const outerRim = trioMode ? outerRadiusOuter : middleRadiusOuter;
  // Kantens stroke (7) sticker ut 3,5 utanför outerRim; i trio-läget är
  // outerRim 350 och skulle annars klippas av viewBoxen. Hjulet skalas då
  // ner runt mitten i stället för att viewBoxen förstoras: varje lager
  // roteras runt sin rutas mitt och den måste vara 350,350, annars
  // vinglar ringarna.
  const rimFit = hol ? Math.min(1, 350 / (outerRim + 3.5)) : 1;
  const fitTransform =
    rimFit < 1 ? `translate(350 350) scale(${rimFit}) translate(-350 -350)` : undefined;
  const centerRadius = trioMode ? 100 : 119;
  const iconSize = trioMode ? 100 : 120;
  const iconOffset = 350 - iconSize / 2;

  // Gästnamnen ligger mitt i bandet mellan mittknappen och innersta ringen.
  // Alla tre får samma storlek: den som får det längsta namnet ("Voldemort")
  // att rymmas i segmentets bredd (kordan vid radien), dock högst 18 så att de
  // är lite mindre än ringarnas vanliga namn (22). De mäts i dolda textrader.
  const guestRadius = (centerRadius + innerRadius) / 2;
  const guestMaxWidth = 2 * guestRadius * (slotCount > 2 ? Math.sin(Math.PI / slotCount) : 1) * 0.82;
  const guestMeasureRefs = React.useRef<(SVGTextElement | null)[]>([]);
  const [guestFontSize, setGuestFontSize] = React.useState(18);
  const hasGuests = guests.length > 0;
  React.useLayoutEffect(() => {
    if (!hasGuests) return;
    const widths = guestMeasureRefs.current.map(el =>
      el && typeof el.getComputedTextLength === "function" ? el.getComputedTextLength() : 0
    );
    const widest = Math.max(0, ...widths);
    if (widest > 0) setGuestFontSize(Math.min(18, (22 * guestMaxWidth) / widest));
  }, [hasGuests, guestMaxWidth]);

  const defaultCenterFill =
    theme === "bw"
      ? "#180f26" /* Dark purple for BW theme */
      : theme === "tomtbola"
        ? "#f27656" /* Red for Tomtbola */
        : "#180f26"; /* Dark purple for Fikahjulet */

  // Vissa högtider byter ikon i mitten när hjulet har stannat.
  const RevealIcon = holiday && settled ? WHEEL_REVEAL_ICONS[holiday] : undefined;
  const HolidayIcon = holiday ? RevealIcon ?? WHEEL_ICONS[holiday] : null;
  const holidayIconSize = iconSize * ((RevealIcon && holiday && WHEEL_REVEAL_SCALE[holiday]) || 1.1);

  // Hjulets kontur utan högtid: ringarna plus mittknappen, med glipan
  // emellan tom (evenodd). Ritas i det stilla baslagret och bär skuggan.
  const circlePath = (r: number) => `M${350 - r} 350a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`;
  const baseShape = `${circlePath(outerRim)}${circlePath(innerRadius)}${circlePath(centerRadius)}`;

  /*
   * Hjulet är staplade svg-lager i stället för en enda svg. Ringarna ritas
   * om i varje bildruta medan de snurrar (det håller text och linjer skarpa),
   * men skuggan under och glansen över ligger i egna, stilla lager som bara
   * målas en gång. Förut målades skuggfiltren om i varje bildruta – det var
   * det som hackade. Varje lager har sina egna gradienter så att "Spara som
   * bild" (html2canvas, som ritar varje svg för sig) hittar dem.
   */
  const layer = (key: string, children: React.ReactNode, rotation?: number, className = "") => (
    <svg
      key={key}
      className={`wheel-layer ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 700 700"
    >
      <g transform={fitTransform}>
        {rotation !== undefined ? (
          <g className="animated-rotation" transform={`rotate(${rotation})`}>
            {children}
          </g>
        ) : (
          children
        )}
      </g>
    </svg>
  );

  return (
    <>
    {/* Skärmen lyser upp när blixten slår ner */}
    {striking && (
      <>
        <div className="wheel-strike-dim" aria-hidden="true" />
        <div className="wheel-strike-flash" aria-hidden="true" />
      </>
    )}
    <div
      className={`WheelSvg ${holiday ? "WheelSvg-holiday" : ""} ${striking ? "wheel-struck" : ""}`}
      onClick={onClick}
    >
      {layer(
        "base",
        hol ? (
          <>
            <defs>
              {/* Fasad kant: ljus upptill, mörk nedtill */}
              <linearGradient id="wheel-rim" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={hol.rimLight} />
                <stop offset="55%" stopColor={hol.rim} />
                <stop offset="100%" stopColor={hol.rimDark} />
              </linearGradient>
            </defs>
            {/* Kant runt hela hjulet så att det står ut mot bakgrunden */}
            <circle
              cx={350}
              cy={350}
              r={outerRim}
              fill={hol.segmentTo}
              stroke="url(#wheel-rim)"
              strokeWidth="7"
            />
          </>
        ) : (
          <path d={baseShape} fillRule="evenodd" fill={themeColors(theme).segmentTo} />
        ),
        undefined,
        "wheel-base"
      )}
      {layer(
        "inner",
        <Ring
          theme={theme}
          strings={innerNames}
          cx={350}
          cy={350}
          innerRadius={innerRadius}
          outerRadius={middleRadiusInner}
          colors={ringColors}
          idPrefix="grad-inner"
        />,
        innerRotation
      )}
      {layer(
        "outer",
        <Ring
          theme={theme}
          strings={outerNames}
          cx={350}
          cy={350}
          innerRadius={middleRadiusInner}
          outerRadius={middleRadiusOuter}
          colors={ringColors}
          idPrefix="grad-outer"
        />,
        outerRotation
      )}
      {trioMode && thirdNames.length > 0 &&
        layer(
          "third",
          <Ring
            theme={theme}
            strings={thirdNames}
            cx={350}
            cy={350}
            innerRadius={middleRadiusOuter}
            outerRadius={outerRadiusOuter}
            colors={ringColors}
            idPrefix="grad-third"
          />,
          thirdRotation
        )}
      {layer(
        "center",
        <>
          {hol && (
            <defs>
              <radialGradient id="wheel-center-gradient" cx="40%" cy="35%" r="75%">
                <stop offset="0%" stopColor={hol.centerFrom} />
                <stop offset="100%" stopColor={hol.centerTo} />
              </radialGradient>
            </defs>
          )}
          <circle
            cx={350}
            cy={350}
            r={centerRadius}
            fill={hol ? "url(#wheel-center-gradient)" : defaultCenterFill}
            filter="drop-shadow(0px 4px 8px rgba(0,0,0,0.3))"
          />
          {hol && (
            <circle
              cx={350}
              cy={350}
              r={centerRadius - 6}
              fill="none"
              stroke={hol.segmentFrom}
              strokeOpacity="0.35"
              strokeWidth="2"
            />
          )}
          {HolidayIcon ? (
            <MemeGuestContext.Provider value={guestContext}>
              <HolidayIcon
                x={350 - holidayIconSize / 2}
                y={350 - holidayIconSize / 2}
                width={holidayIconSize}
                height={holidayIconSize}
              />
            </MemeGuestContext.Provider>
          ) : theme === "tomtbola" ? (
            <text
              x="50%"
              y="50%"
              dominantBaseline="middle"
              textAnchor="middle"
              fill="white"
              fontSize="32"
              fontWeight="700"
              fontFamily="'Geist Sans', sans-serif"
            >
              Starta
            </text>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 2429.18 2429.18"
              width={iconSize}
              height={iconSize}
              fill="#ffffff" /* White fill */
              x={iconOffset}
              y={iconOffset}
            >
              <path d="M1274.26 1408.53h-.51V183.86c400.12 16.18 741.14 261.78 895.08 608.8h91.24c-159.21-394.49-538.54-676.54-985.81-693.9q-21.81-.86-43.81-.85c-613 0-1109.89 496.91-1109.89 1109.89s496.91 1109.89 1109.88 1109.89c544.43 0 997.29-392 1091.77-909.16ZM206.81 1207.8c0-433.85 269.6-804.72 650.39-954.25-158 128.5-259.06 325.16-259.06 544.81 0 313.89 206.41 577.31 490.37 664.25L478 1902.25c-168.34-182.59-271.19-426.51-271.19-694.45m982.66 1023.93c-250.71-10.14-478.2-110.35-651-269.14l651-468.83Zm0-823.2c-289.14-48.66-509.29-305.74-509.29-610.17 0-303.25 218.76-563.56 506.24-614.37l3.36-.14Zm84.28 823.21v-738.93h942.48c-119.74 414.39-493.97 720.79-942.48 738.93" />
            </svg>
          )}
        </>,
        buttonRotation
      )}
      {hol && guests.length > 0 && slotCount > 0 &&
        layer(
          "guests",
          <>
          <g visibility="hidden" aria-hidden="true">
            {MEME_GUEST_NAMES.map((name, i) => (
              <text
                key={name}
                ref={el => {
                  guestMeasureRefs.current[i] = el;
                }}
                fontSize={22}
                fontFamily="'Geist Sans', sans-serif"
                fontWeight={600}
              >
                {name}
              </text>
            ))}
          </g>
          {guests.map(({ name, slot }) => {
            // Bandet mellan mittknappen och den innersta ringen, i linje med
            // gruppens segment. Texten vänds som i Ring så att den ser ut att höra dit.
            const slotAngle = 360 / slotCount;
            const middleAngle = (slot + 0.5) * slotAngle;
            const pos = polarToCartesian(350, 350, guestRadius, middleAngle);
            const start = {
              "--guest-dx": `${350 - pos.x}px`,
              "--guest-dy": `${350 - pos.y}px`,
            } as React.CSSProperties;
            return (
              <g key={name} pointerEvents="none">
                {/* Hela gruppen blixtrar till när gästen landar */}
                <path
                  className="wheel-guest-flash"
                  d={describeSegment(350, 350, centerRadius + 3, outerRim - 3, slot * slotAngle, (slot + 1) * slotAngle)}
                  fill="#ff3c14"
                  fillOpacity="0.14"
                  stroke="#ff3c14"
                  strokeWidth="4"
                  strokeLinejoin="round"
                  opacity={0}
                />
                {/* Namnet flyger ut ur memen i mitten till sin plats */}
                <g className="wheel-guest" style={start}>
                  <text
                    className="wheel-guest-name"
                    x={pos.x}
                    y={pos.y}
                    fill={hol.text}
                    fontSize={guestFontSize}
                    fontFamily="'Geist Sans', sans-serif"
                    fontWeight={600}
                    textAnchor="middle"
                    dy=".3em"
                    transform={`rotate(${middleAngle} ${pos.x} ${pos.y})`}
                  >
                    {name}
                  </text>
                </g>
              </g>
            );
          })}
          </>,
          undefined,
          "wheel-guests"
        )}
      {(hol || striking) &&
        layer(
          "overlay",
          <>
            {hol && (
              /* Ljus och skugga ovanpå hela skivan – ger hjulet volym */
              <g pointerEvents="none">
                <defs>
                  {/* Glansig yta: ljusreflex uppe till vänster */}
                  <radialGradient id="wheel-sheen" cx="34%" cy="26%" r="72%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.34" />
                    <stop offset="42%" stopColor="#ffffff" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient id="wheel-shade" cx="72%" cy="80%" r="70%">
                    <stop offset="0%" stopColor={hol.rimDark} stopOpacity="0.2" />
                    <stop offset="55%" stopColor={hol.rimDark} stopOpacity="0" />
                  </radialGradient>
                </defs>
                <circle cx={350} cy={350} r={outerRim - 3} fill="url(#wheel-shade)" />
                <circle cx={350} cy={350} r={outerRim - 3} fill="url(#wheel-sheen)" />
                <circle cx={350} cy={350} r={outerRim - 5} fill="none" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1.5" />
                <circle cx={350} cy={350} r={centerRadius + 3} fill="none" stroke={hol.rimDark} strokeOpacity="0.25" strokeWidth="3" />
              </g>
            )}
            {striking && <LightningStrike />}
          </>,
          undefined,
          "wheel-overlay"
        )}
    </div>
    </>
  );
};

export default Wheel;
